// The rules the trainer dashboard applies to `/instructors/me/dashboard-summary`.
// Pure, so which class is "next" and what the day's summary says are pinned by
// tests rather than re-decided in a card.
//
// Class times are the batch's own "HH:MM" and are compared with the BROWSER's
// clock. The server's `today.nextClass` is not used: the box runs in UTC, so its
// idea of "now" is five and a half hours behind an Indian classroom.

const pad = (n) => String(n).padStart(2, '0');

export const formatTime = (time24) => {
  if (!time24) return '';
  const [h, m] = String(time24).split(':').map(Number);
  if (Number.isNaN(h)) return '';
  const period = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${pad(m || 0)} ${period}`;
};

export const timeRange = (start, end) => {
  const s = formatTime(start);
  const e = formatTime(end);
  if (s && e) return `${s} – ${e}`;
  return s || e || '';
};

/** "HH:MM" on the local clock — the same shape a batch stores its times in. */
export const clockHM = (date = new Date()) => `${pad(date.getHours())}:${pad(date.getMinutes())}`;

/** "YYYY-MM-DD" on the local clock. `toISOString()` is UTC, which names
 *  yesterday for anything before 05:30 IST. */
export const localDateKey = (date = new Date()) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

export const greeting = (date = new Date()) => {
  const h = date.getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};

export const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** "10 min ago", "2 hours ago", "3 days ago"; a date once it is a month old. */
export const timeAgo = (date, now = Date.now()) => {
  if (!date) return '';
  const then = new Date(date).getTime();
  if (Number.isNaN(then)) return '';
  const secs = Math.max(0, Math.round((now - then) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${plural(hours, 'hour', 'hours')} ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${plural(days, 'day', 'days')} ago`;
  if (days < 30) return `${plural(Math.round(days / 7), 'week', 'weeks')} ago`;
  return new Date(date).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

const MODE_LABELS = { Online: 'Online', Offline: 'Offline', Both: 'Online & offline' };

/** A batch's `mode` as a phrase; "Both" alone does not say both of what. */
export const modeLabel = (mode) => MODE_LABELS[mode] || mode || '';

/** Whether a batch has an online room to start or rejoin. */
export const isOnlineBatch = (b) =>
  b?.mode === 'Online' || b?.mode === 'Both' || !!b?.meetingLink || !!b?.zoomMeetingId;

/**
 * The bar attendance is read against. The payload does not carry a batch's own
 * `minAttendancePct`, so this is the default every batch starts with.
 */
export const ATTENDANCE_BAR = 75;

/** `warning` below the bar, otherwise nothing — colour only when it needs a look. */
export const attendanceTone = (pct) =>
  (pct === null || pct === undefined ? null : pct < ATTENDANCE_BAR ? 'warning' : null);

/**
 * The day's classes in order, each with where it stands:
 *
 *   live     — the room is open now
 *   done     — today's, and its end time has passed
 *   next     — the first of today's classes still to come; at most one
 *   later    — today's, after the next one
 *   upcoming — tomorrow's (the server falls back to tomorrow when today is empty)
 *
 * A batch that is live but NOT on the list — a weekend batch opened on a
 * weekday, a class started early — is put first, so the one room a trainer
 * must get back into is never missing from the card.
 *
 * Each item carries its batch row, which is what the class controls need
 * (`status`, `isLive`, the room fields). Schedule entries are a subset of the
 * ongoing batches, so the lookup always finds one; `batch` is null otherwise.
 */
export function buildAgenda({ today = {}, batches = [], now = clockHM() } = {}) {
  const schedule = Array.isArray(today.schedule) ? today.schedule : [];
  const byId = new Map(batches.map((b) => [String(b._id), b]));
  const listed = new Set(schedule.map((s) => String(s._id)));

  const toItem = (entry, batch, state) => ({
    id: String(entry._id),
    name: entry.name || '',
    course: entry.course || '',
    startTime: entry.startTime || '',
    endTime: entry.endTime || '',
    mode: entry.mode || '',
    studentCount: batch?.studentCount ?? null,
    batch: batch || null,
    state,
  });

  const items = batches
    .filter((b) => b.isLive && !listed.has(String(b._id)))
    .map((b) => toItem(b, b, 'live'));

  let nextTaken = false;
  schedule.forEach((entry) => {
    const batch = byId.get(String(entry._id)) || null;
    let state;
    if (entry.isLive || batch?.isLive) state = 'live';
    else if (today.isTomorrow) state = 'upcoming';
    else if (entry.endTime && entry.endTime < now) state = 'done';
    else if (!nextTaken) { state = 'next'; nextTaken = true; }
    else state = 'later';
    items.push(toItem(entry, batch, state));
  });

  return items;
}

/** A `next` class whose start time has passed has not been started yet. */
export const isOverdue = (item, now = clockHM()) =>
  item?.state === 'next' && !!item.startTime && item.startTime <= now;

/** The one line under the greeting: how the day looks from here. */
export function daySummary(today = {}, agenda = []) {
  const count = Array.isArray(today.schedule) ? today.schedule.length : 0;
  const live = agenda.find((i) => i.state === 'live');

  if (today.isTomorrow) {
    const first = agenda.find((i) => i.state === 'upcoming');
    const at = first?.startTime ? `, first at ${formatTime(first.startTime)}` : '';
    const lead = live ? `${live.name} is live` : 'No classes today';
    return `${lead} · ${plural(count, 'class', 'classes')} tomorrow${at}`;
  }
  if (!count) return live ? `${live.name} is live` : 'No classes scheduled today';

  const next = agenda.find((i) => i.state === 'next');
  let tail = 'all done for the day';
  if (live) tail = `${live.name} is live`;
  else if (next) tail = next.startTime ? `next at ${formatTime(next.startTime)}` : `next: ${next.name}`;
  return `${plural(count, 'class', 'classes')} today · ${tail}`;
}

/** Live first, then by start time; batches with no time last. */
export const orderBatches = (batches = []) =>
  batches.slice().sort((a, b) => {
    if (!!a.isLive !== !!b.isLive) return a.isLive ? -1 : 1;
    const at = a.startTime || '99:99';
    const bt = b.startTime || '99:99';
    return at.localeCompare(bt);
  });
